import { useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Calendar } from "./ui/calendar";

export const PageHeader = ({ title, subtitle, action }) => (
  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-6 fade-up">
    <div>
      <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900">{title}</h1>
      {subtitle && <p className="text-sm text-slate-500 mt-1">{subtitle}</p>}
    </div>
    {action}
  </div>
);

export const StatusBadge = ({ status }) => {
  const styles = {
    active: "bg-emerald-50 text-emerald-700 border-emerald-200",
    approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
    paused: "bg-amber-50 text-amber-700 border-amber-200",
    pending: "bg-amber-50 text-amber-700 border-amber-200",
    rejected: "bg-rose-50 text-rose-700 border-rose-200",
    inactive: "bg-slate-100 text-slate-600 border-slate-200",
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold border ${styles[status] || styles.inactive}`}>
      {status}
    </span>
  );
};

export const money = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
export const fmtDate = (iso) =>
  new Date(iso).toLocaleString("en-US", { timeZone: "America/New_York", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) + " EST";

export const Th = ({ children, className = "" }) => (
  <th className={`text-left px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-slate-500 bg-slate-50/80 ${className}`}>{children}</th>
);
export const Td = ({ children, className = "", ...props }) => (
  <td className={`px-4 py-2.5 text-[13px] text-slate-700 ${className}`} {...props}>{children}</td>
);

export const Card = ({ children, className = "", ...props }) => (
  <div className={`bg-white border border-slate-200/80 rounded-xl ${className}`} {...props}>{children}</div>
);

export const RANGE_OPTIONS = [
  { v: "today", l: "Today" },
  { v: "yesterday", l: "Yesterday" },
  { v: "this_week", l: "This Week" },
  { v: "last_week", l: "Last Week" },
  { v: "this_month", l: "This Month" },
  { v: "last_month", l: "Last Month" },
];

export const rangeParams = (r) =>
  `range=${r.range}` + (r.range === "custom" && r.from ? `&from_date=${r.from}&to_date=${r.to || r.from}` : "");

const fmtYmd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export const DateRangeFilter = ({ value, onChange }) => {
  const [sel, setSel] = useState();
  const [open, setOpen] = useState(false);
  const btn = (active) =>
    `px-3 py-1 text-xs font-semibold rounded-md transition-colors ${active ? "bg-indigo-600 text-white" : "text-slate-600 hover:bg-slate-100"}`;
  return (
    <div className="inline-flex items-center gap-1 bg-white border border-slate-200 rounded-lg p-1 flex-wrap" data-testid="date-range-filter">
      {RANGE_OPTIONS.map((o) => (
        <button key={o.v} data-testid={`range-${o.v}`} onClick={() => onChange({ range: o.v })} className={btn(value.range === o.v)}>
          {o.l}
        </button>
      ))}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button data-testid="range-custom" className={btn(value.range === "custom")}>
            {value.range === "custom" && value.from ? `${value.from} → ${value.to || value.from}` : "Custom"}
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-2" align="end">
          <Calendar mode="range" selected={sel} onSelect={setSel} numberOfMonths={1} />
          <button
            data-testid="range-custom-apply"
            disabled={!sel?.from}
            onClick={() => {
              onChange({ range: "custom", from: fmtYmd(sel.from), to: fmtYmd(sel.to || sel.from) });
              setOpen(false);
            }}
            className="w-full mt-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-xs font-semibold rounded-md py-2 transition-colors"
          >
            Apply Range
          </button>
        </PopoverContent>
      </Popover>
    </div>
  );
};

const IP_STYLES = {
  us: { l: "Real US", c: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  vpn: { l: "VPN / Proxy", c: "bg-rose-50 text-rose-700 border-rose-200" },
  non_us: { l: "Non-US", c: "bg-amber-50 text-amber-700 border-amber-200" },
  unknown: { l: "Unknown", c: "bg-slate-100 text-slate-600 border-slate-200" },
};

export const IP_TYPE_OPTIONS = [
  { v: "us", l: "Real US" }, { v: "vpn", l: "VPN / Proxy" }, { v: "non_us", l: "Non-US" }, { v: "unchecked", l: "Not checked" },
];

export const ipLocation = (c) => (c ? [c.city, [c.region_code || c.region, c.postal].filter(Boolean).join(" "), c.country].filter(Boolean).join(", ") : "");

export const IpTag = ({ check, ip, showIp = true, testId, stacked = false }) => {
  const s = check ? IP_STYLES[check.type] || IP_STYLES.unknown : null;
  const where = ipLocation(check);
  const title = check ? `${where} · ${check.isp || "?"} · ${check.connection_type || "?"} · risk ${check.fraud_score ?? "?"}${check.timezone ? ` · ${check.timezone}` : ""}` : "Not checked yet";
  return (
    <span data-testid={testId} title={title} className={`inline-flex gap-1 whitespace-nowrap ${stacked ? "flex-col items-start" : "items-center gap-1.5 flex-wrap"}`}>
      <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold border ${s ? s.c : "bg-white text-slate-400 border-dashed border-slate-300"}`}>
        {s ? `${s.l}${check.type === "non_us" && check.country ? ` · ${check.country}` : ""}` : "Not checked"}
      </span>
      {showIp && ip && <code className="text-[10px] text-slate-500">{ip}</code>}
      {where && <span data-testid={testId && `${testId}-location`} className="text-[10px] text-slate-500">{where}</span>}
    </span>
  );
};

const LEAD_STYLES = {
  found: { l: "In Lead DB", c: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  not_found: { l: "NOT AVAILABLE", c: "bg-rose-50 text-rose-700 border-rose-200" },
  pending: { l: "Checking…", c: "bg-white text-slate-600 border-dashed border-slate-400 animate-pulse" },
  error: { l: "Lookup failed — retrying", c: "bg-slate-50 text-slate-600 border-slate-300" },
  no_contact: { l: "No email/phone", c: "bg-slate-100 text-slate-600 border-slate-300" },
  anonymous: { l: "Anonymous", c: "bg-slate-100 text-slate-600 border-slate-300" },
};

export const LeadTag = ({ lead, testId }) => {
  if (!lead || lead.status === "off") return null;
  const s = LEAD_STYLES[lead.status] || LEAD_STYLES.anonymous;
  const title = lead.status === "found"
    ? `Lead portal match by ${lead.matched_by}: ${lead.name || "—"}${lead.lead_state ? ` (${lead.lead_state})` : ""}${lead.file_name ? ` · ${lead.file_name}` : ""}`
    : lead.status === "not_found" ? "Not found in your lead portal" : "";
  return (
    <span data-testid={testId} title={title} className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold border whitespace-nowrap ${s.c}`}>
      {s.l}
    </span>
  );
};

export const hasPendingLead = (items) => items.some((i) => i.lead_db?.status === "pending");
